import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useFetch, fmtDate } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { Sheet } from '../_primitives/Sheet';
import { ConfirmDialog } from '../_primitives/ConfirmDialog';
import type { PropertyRow } from '../types';

export const PropertiesTab: React.FC = () => {
  const { data, loading, error, refresh } = useFetch(adminApi.properties);
  const [drilldown, setDrilldown] = useState<PropertyRow | null>(null);
  const [deleting, setDeleting] = useState<PropertyRow | null>(null);

  const cols: Column<PropertyRow>[] = [
    { key: 'address', label: 'Adres', sortValue: (p) => p.address, searchValue: (p) => p.address,
      render: (p) => <span className="font-medium text-ink dark:text-white truncate max-w-[36ch] inline-block" title={p.address}>{p.address}</span> },
    { key: 'email', label: 'Eigenaar', sortValue: (p) => p.email ?? '', searchValue: (p) => p.email ?? '',
      render: (p) => <span className="text-ink/65 dark:text-white/65">{p.email ?? '—'}</span> },
    { key: 'render_count', label: 'Renders', align: 'right', sortValue: (p) => p.render_count,
      render: (p) => <span className="font-mono text-ink/75 dark:text-white/75">{p.render_count}</span> },
    { key: 'created_at', label: 'Aangemaakt', sortValue: (p) => p.created_at,
      render: (p) => <span className="text-ink/55 dark:text-white/55">{fmtDate(p.created_at)}</span> },
  ];

  const runDelete = async () => {
    if (!deleting) return;
    try {
      await adminApi.deleteProperty(deleting.id);
      toast.success(`Woning "${deleting.address}" verwijderd`);
      refresh();
      setDeleting(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Verwijderen mislukt');
    }
  };

  return (
    <>
      <Section title={`Woningen (${(data ?? []).length})`}>
        <DataTable
          rows={data ?? []}
          loading={loading}
          error={error}
          onRetry={refresh}
          columns={cols}
          rowKey={(p) => p.id}
          searchPlaceholder="Zoek op adres of eigenaar…"
          onRowClick={(p) => setDrilldown(p)}
        />
      </Section>

      <Sheet open={!!drilldown} onClose={() => setDrilldown(null)} title={drilldown?.address} subtitle={drilldown?.email ?? undefined}>
        {drilldown && (
          <div className="p-5 space-y-4">
            <dl className="grid grid-cols-2 gap-3 text-xs">
              <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">ID</dt><dd className="font-mono mt-0.5">{drilldown.id}</dd></div>
              <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">User ID</dt><dd className="font-mono mt-0.5">{drilldown.user_id}</dd></div>
              <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Renders</dt><dd className="font-mono mt-0.5">{drilldown.render_count}</dd></div>
              <div><dt className="text-ink/45 dark:text-white/45 uppercase tracking-wider font-semibold">Aangemaakt</dt><dd className="mt-0.5">{fmtDate(drilldown.created_at)}</dd></div>
            </dl>
            <button
              type="button"
              onClick={() => setDeleting(drilldown)}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-red-300 dark:border-red-900 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold transition-colors"
            >
              <Trash2 size={12} /> Verwijder woning
            </button>
          </div>
        )}
      </Sheet>

      <ConfirmDialog
        open={!!deleting}
        destructive
        typedConfirmation="DELETE"
        title="Woning verwijderen"
        description={deleting && <p>Adres <span className="font-mono">{deleting.address}</span> en alle bijbehorende renders worden permanent verwijderd.</p>}
        confirmLabel="Verwijder"
        onConfirm={runDelete}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
};
